import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, X, Loader2, Sparkles, MapPin, FileText, DollarSign, MessageCircle, Users, Star, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AIAssistant({ token, userRole, onQuoteSuggestion, onJobDescriptionImproved, onCarrierMatched }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [loading, setLoading] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Quote suggestion state
  const [quoteJobId, setQuoteJobId] = useState('');
  const [quoteSuggestion, setQuoteSuggestion] = useState(null);
  
  // Job description state
  const [jobDescForm, setJobDescForm] = useState({
    job_type: 'household',
    pickup_city: '',
    pickup_state: '',
    delivery_city: '',
    delivery_state: '',
    items: '',
    special_requirements: ''
  });
  const [improvedDescription, setImprovedDescription] = useState(null);
  
  // Route estimate state
  const [routeForm, setRouteForm] = useState({
    pickup_city: '',
    pickup_state: '',
    delivery_city: '',
    delivery_state: '',
    job_type: 'freight',
    weight_estimate: ''
  });
  const [routeEstimate, setRouteEstimate] = useState(null);

  // Carrier matching state
  const [matchJobId, setMatchJobId] = useState('');
  const [carrierMatches, setCarrierMatches] = useState(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendChatMessage = async () => {
    if (!chatInput.trim() || loading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/chat`, {
        message: userMessage,
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const getQuoteSuggestion = async () => {
    if (!quoteJobId.trim()) {
      toast.error('Please enter a Job ID');
      return;
    }
    
    setLoading(true);
    setQuoteSuggestion(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/quote-suggestion`, {
        job_id: quoteJobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuoteSuggestion(response.data);
      if (onQuoteSuggestion) onQuoteSuggestion(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get quote suggestion');
    } finally {
      setLoading(false);
    }
  };

  const improveJobDescription = async () => {
    if (!jobDescForm.pickup_city || !jobDescForm.delivery_city) {
      toast.error('Please fill in pickup and delivery cities');
      return;
    }
    
    setLoading(true);
    setImprovedDescription(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/improve-job-description`, jobDescForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImprovedDescription(response.data.improved_description);
      if (onJobDescriptionImproved) onJobDescriptionImproved(response.data.improved_description);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to improve description');
    } finally {
      setLoading(false);
    }
  };

  const getRouteEstimate = async () => {
    if (!routeForm.pickup_city || !routeForm.delivery_city) {
      toast.error('Please fill in pickup and delivery cities');
      return;
    }
    
    setLoading(true);
    setRouteEstimate(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/route-estimate`, {
        ...routeForm,
        weight_estimate: routeForm.weight_estimate ? parseFloat(routeForm.weight_estimate) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRouteEstimate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get route estimate');
    } finally {
      setLoading(false);
    }
  };

  const findMatchingCarriers = async () => {
    if (!matchJobId.trim()) {
      toast.error('Please enter a Job ID');
      return;
    }
    
    setLoading(true);
    setCarrierMatches(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/match-carriers`, {
        job_id: matchJobId,
        max_carriers: 5
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCarrierMatches(response.data);
      if (onCarrierMatched) onCarrierMatched(response.data);
      toast.success(`Found ${response.data.matches?.length || 0} matching carriers!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to find matching carriers');
    } finally {
      setLoading(false);
    }
  };

  const autoMatchAndNotify = async () => {
    if (!matchJobId.trim()) {
      toast.error('Please enter a Job ID');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/auto-match-job/${matchJobId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCarrierMatches(response.data);
      toast.success(`Notified ${response.data.carriers_notified} carriers about your job!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to notify carriers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating AI Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
        data-testid="ai-assistant-btn"
      >
        <Sparkles className="h-6 w-6" />
      </button>

      {/* AI Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-orange-500" />
              AI Assistant
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className={`grid ${userRole === 'customer' ? 'grid-cols-4' : 'grid-cols-3'} mb-4`}>
              <TabsTrigger value="chat" className="flex items-center gap-1">
                <MessageCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Chat</span>
              </TabsTrigger>
              {userRole === 'carrier' && (
                <TabsTrigger value="quote" className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  <span className="hidden sm:inline">Quote</span>
                </TabsTrigger>
              )}
              {userRole === 'customer' && (
                <TabsTrigger value="description" className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Job</span>
                </TabsTrigger>
              )}
              {userRole === 'customer' && (
                <TabsTrigger value="match" className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Match</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="route" className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span className="hidden sm:inline">Route</span>
              </TabsTrigger>
            </TabsList>

            {/* Chat Tab */}
            <TabsContent value="chat" className="flex-1 flex flex-col overflow-hidden m-0">
              <div className="flex-1 overflow-y-auto space-y-3 p-2 bg-slate-50 rounded-lg min-h-[300px] max-h-[400px]">
                {messages.length === 0 && (
                  <div className="text-center text-slate-500 py-8">
                    <Bot className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>Hi! I'm your AI assistant. Ask me anything about True Choice Enterprise!</p>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-lg ${
                      msg.role === 'user' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-white border border-slate-200'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-200 p-3 rounded-lg">
                      <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="flex gap-2 mt-3">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Ask me anything..."
                  disabled={loading}
                  data-testid="ai-chat-input"
                />
                <Button onClick={sendChatMessage} disabled={loading || !chatInput.trim()} data-testid="ai-chat-send">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </TabsContent>

            {/* Quote Suggestion Tab (Carriers only) */}
            {userRole === 'carrier' && (
              <TabsContent value="quote" className="flex-1 overflow-y-auto m-0">
                <div className="space-y-4 p-2">
                  <div>
                    <label className="text-sm font-medium">Job ID</label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={quoteJobId}
                        onChange={(e) => setQuoteJobId(e.target.value)}
                        placeholder="Enter job ID..."
                        data-testid="quote-job-id-input"
                      />
                      <Button onClick={getQuoteSuggestion} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Get Suggestion'}
                      </Button>
                    </div>
                  </div>
                  
                  {quoteSuggestion && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2">💰 Quote Recommendation</h4>
                      {quoteSuggestion.suggestion.low_price && (
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-sm text-slate-500">Low</div>
                            <div className="text-lg font-bold text-green-600">${quoteSuggestion.suggestion.low_price}</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded border-2 border-green-400">
                            <div className="text-sm text-slate-500">Recommended</div>
                            <div className="text-xl font-bold text-green-700">${quoteSuggestion.suggestion.mid_price}</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-sm text-slate-500">High</div>
                            <div className="text-lg font-bold text-green-600">${quoteSuggestion.suggestion.high_price}</div>
                          </div>
                        </div>
                      )}
                      <p className="text-sm text-green-700">{quoteSuggestion.suggestion.reasoning || quoteSuggestion.suggestion.raw_response}</p>
                      <p className="text-xs text-slate-500 mt-2 italic">{quoteSuggestion.disclaimer}</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Job Description Tab (Customers only) */}
            {userRole === 'customer' && (
              <TabsContent value="description" className="flex-1 overflow-y-auto m-0">
                <div className="space-y-3 p-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Job Type</label>
                      <select
                        value={jobDescForm.job_type}
                        onChange={(e) => setJobDescForm({...jobDescForm, job_type: e.target.value})}
                        className="w-full mt-1 p-2 border rounded-md"
                      >
                        <option value="household">Household Move</option>
                        <option value="freight">Freight</option>
                        <option value="cargo">Cargo</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Pickup City"
                      value={jobDescForm.pickup_city}
                      onChange={(e) => setJobDescForm({...jobDescForm, pickup_city: e.target.value})}
                    />
                    <Input
                      placeholder="Pickup State"
                      value={jobDescForm.pickup_state}
                      onChange={(e) => setJobDescForm({...jobDescForm, pickup_state: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Delivery City"
                      value={jobDescForm.delivery_city}
                      onChange={(e) => setJobDescForm({...jobDescForm, delivery_city: e.target.value})}
                    />
                    <Input
                      placeholder="Delivery State"
                      value={jobDescForm.delivery_state}
                      onChange={(e) => setJobDescForm({...jobDescForm, delivery_state: e.target.value})}
                    />
                  </div>
                  <Textarea
                    placeholder="What items need to be moved?"
                    value={jobDescForm.items}
                    onChange={(e) => setJobDescForm({...jobDescForm, items: e.target.value})}
                  />
                  <Button onClick={improveJobDescription} disabled={loading} className="w-full">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Generate Professional Description
                  </Button>
                  
                  {improvedDescription && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">✨ Improved Description</h4>
                      <div className="space-y-2 text-sm">
                        {improvedDescription.title && (
                          <div><span className="font-medium">Title:</span> {improvedDescription.title}</div>
                        )}
                        {improvedDescription.description && (
                          <div><span className="font-medium">Description:</span> {improvedDescription.description}</div>
                        )}
                        {improvedDescription.special_instructions && (
                          <div><span className="font-medium">Special Instructions:</span> {improvedDescription.special_instructions}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Route Estimate Tab */}
            <TabsContent value="route" className="flex-1 overflow-y-auto m-0">
              <div className="space-y-3 p-2">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Pickup City"
                    value={routeForm.pickup_city}
                    onChange={(e) => setRouteForm({...routeForm, pickup_city: e.target.value})}
                  />
                  <Input
                    placeholder="Pickup State"
                    value={routeForm.pickup_state}
                    onChange={(e) => setRouteForm({...routeForm, pickup_state: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Delivery City"
                    value={routeForm.delivery_city}
                    onChange={(e) => setRouteForm({...routeForm, delivery_city: e.target.value})}
                  />
                  <Input
                    placeholder="Delivery State"
                    value={routeForm.delivery_state}
                    onChange={(e) => setRouteForm({...routeForm, delivery_state: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={routeForm.job_type}
                    onChange={(e) => setRouteForm({...routeForm, job_type: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="household">Household Move</option>
                    <option value="freight">Freight</option>
                    <option value="cargo">Cargo</option>
                  </select>
                  <Input
                    type="number"
                    placeholder="Weight (lbs)"
                    value={routeForm.weight_estimate}
                    onChange={(e) => setRouteForm({...routeForm, weight_estimate: e.target.value})}
                  />
                </div>
                <Button onClick={getRouteEstimate} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MapPin className="h-4 w-4 mr-2" />}
                  Get Route Estimate
                </Button>
                
                {routeEstimate && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">🗺️ Route Estimate</h4>
                    <p className="text-sm text-purple-700 mb-2">{routeEstimate.route}</p>
                    {routeEstimate.estimate.estimated_days && (
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div className="text-center p-2 bg-white rounded">
                          <div className="text-2xl font-bold text-purple-600">{routeEstimate.estimate.estimated_days}</div>
                          <div className="text-sm text-slate-500">Days</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <div className="text-2xl font-bold text-purple-600">{routeEstimate.estimate.estimated_hours_driving}</div>
                          <div className="text-sm text-slate-500">Driving Hours</div>
                        </div>
                      </div>
                    )}
                    {routeEstimate.estimate.route_notes && (
                      <p className="text-sm text-purple-700">{routeEstimate.estimate.route_notes}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-2 italic">{routeEstimate.disclaimer}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Carrier Matching Tab (Customers only) */}
            {userRole === 'customer' && (
              <TabsContent value="match" className="flex-1 overflow-y-auto m-0">
                <div className="space-y-4 p-2">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-800">
                      <strong>🎯 AI-Powered Carrier Matching</strong><br/>
                      Enter your Job ID to find the best carriers based on location, ratings, expertise, and pricing history.
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Job ID</label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={matchJobId}
                        onChange={(e) => setMatchJobId(e.target.value)}
                        placeholder="Enter your job ID..."
                        data-testid="match-job-id-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={findMatchingCarriers} disabled={loading} className="flex-1">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
                      Find Carriers
                    </Button>
                    <Button onClick={autoMatchAndNotify} disabled={loading} variant="outline" className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Notify Carriers
                    </Button>
                  </div>
                  
                  {carrierMatches && carrierMatches.matches && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-900">
                          Top {carrierMatches.matches.length} Matching Carriers
                        </h4>
                        <span className="text-xs text-slate-500">
                          {carrierMatches.total_carriers_analyzed} analyzed
                        </span>
                      </div>
                      
                      {carrierMatches.matches.map((match, index) => (
                        <div key={match.carrier.id} className="bg-white border rounded-lg p-4 shadow-sm">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                                index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                              }`}>
                                {match.rank}
                              </div>
                              <div>
                                <h5 className="font-semibold text-slate-900">{match.carrier.company_name}</h5>
                                <p className="text-xs text-slate-500">DOT: {match.carrier.dot_number}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-orange-500">{match.match_score}%</div>
                              <div className="text-xs text-slate-500">Match Score</div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-slate-400" />
                              <span>{match.carrier.distance_miles ? `${match.carrier.distance_miles} mi` : 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Star className="h-3 w-3 text-yellow-500" />
                              <span>{match.carrier.rating}/5 ({match.carrier.review_count})</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              <span>{match.carrier.completed_jobs} jobs</span>
                            </div>
                          </div>
                          
                          <p className="text-sm text-slate-600 mb-2">{match.reasoning}</p>
                          
                          {match.strengths && match.strengths.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {match.strengths.map((strength, i) => (
                                <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                  {strength}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {match.considerations && match.considerations.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {match.considerations.map((consideration, i) => (
                                <span key={i} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {consideration}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-slate-500">
                            <span>FMCSA: {match.carrier.fmcsa_status}</span>
                            <span>COI: {match.carrier.coi_status}</span>
                            {match.carrier.expertise_match && (
                              <span className="text-green-600 font-medium">✓ Expertise Match</span>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      <p className="text-xs text-slate-500 italic text-center">
                        {carrierMatches.disclaimer}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AIAssistant;
